/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import {
  GEOWEB_ROLE_PRESETS_ADMIN,
  useAuthenticationContext,
} from '@opengeoweb/authentication';
import type { SystemScope } from '@opengeoweb/shared';
import { snackbarActions, snackbarTypes } from '@opengeoweb/snackbar';
import { PastePresetDialog } from '../PastePresetDialog';
import { WorkspaceOptions } from './WorkspaceOptions';
import type { AppStore, WorkspaceModuleStore } from '../../store/types';
import { workspaceActions } from '../../store/workspace/reducer';
import {
  getSelectedWorkspaceScope,
  getWorkspaceData,
  getViewIds,
  getViewById,
  getWorkspaceId,
  getWorkspaceToSendToBackend,
} from '../../store/workspace/selectors';
import { WorkspacePresetAction } from '../../store/workspace/types';
import type { WorkspacePresetForBE } from '../../store/workspace/types';
import { getLoadingWorkspace } from '../../store/workspace/utils';
import { workspaceListActions } from '../../store/workspaceList/reducer';
import type { WorkspaceActionDialogType } from '../../store/workspaceList/types';
import {
  sanitizeWorkspaceForClipboard,
  writeToClipboard,
} from '../../utils/clipboardUtils';
import { useWorkspaceTranslation } from '../../utils/i18n';

export interface WorkspaceOptionsConnectProps {
  title: string;
  workspaceId: string;
}

export const WorkspaceOptionsConnect: React.FC<
  WorkspaceOptionsConnectProps
> = ({ title, workspaceId }) => {
  const dispatch = useDispatch();
  const store = useStore<WorkspaceModuleStore>();
  const { t } = useWorkspaceTranslation();
  const { currentRole, auth } = useAuthenticationContext();
  const isAdmin = currentRole?.name === GEOWEB_ROLE_PRESETS_ADMIN.name;
  const { isLoggedIn } = useAuthenticationContext();
  const workspaceScope = useSelector((store: AppStore) =>
    getSelectedWorkspaceScope(store),
  );
  const workspaceData = useSelector((store: AppStore) =>
    getWorkspaceData(store),
  );

  const workspaceCreator = workspaceData?.username ?? '';
  const viewIds = useSelector((store: AppStore) => getViewIds(store));

  const storeWorkspaceId = useSelector((store: AppStore) =>
    getWorkspaceId(store),
  );
  const isNew = !storeWorkspaceId;
  const isUserPreset = workspaceScope === 'user';
  const isUserPresetCreator = auth?.username === workspaceCreator;

  const setWorkspaceActionDialogOptions = React.useCallback(
    (dialogOptions: WorkspaceActionDialogType) => {
      dispatch(
        workspaceListActions.openWorkspaceActionDialogOptions(dialogOptions),
      );
    },
    [dispatch],
  );

  const onDelete = (): void => {
    setWorkspaceActionDialogOptions({
      action: WorkspacePresetAction.DELETE,
      presetId: workspaceId,
      formValues: { title },
    });
  };

  const onSaveAction = (
    action: WorkspacePresetAction,
    scope: SystemScope,
    shouldIncludeViewPresetScope = false,
  ): void => {
    const emptyMapPresetTitle = t('workspace-mappreset-new');

    const views = workspaceData.views.map((view) => {
      const viewScope =
        getViewById(store.getState(), view.mosaicNodeId)?.scope ?? 'system';

      const normalizedTitle =
        !view.title || view.title === emptyMapPresetTitle
          ? view.mosaicNodeId
          : view.title;

      return {
        ...view,
        title: normalizedTitle,
        isSelected: false,
        ...(shouldIncludeViewPresetScope && {
          scope: viewScope,
        }),
      };
    });

    const adminMetadata =
      isAdmin && action === WorkspacePresetAction.SAVE_SYSTEM_PRESET_AS
        ? { username: auth?.username }
        : {};

    setWorkspaceActionDialogOptions({
      action,
      presetId: workspaceId,
      formValues: {
        ...workspaceData,
        scope,
        views,
        ...adminMetadata,
      },
      hasSaved: false,
    });
  };

  const onSaveAs = (): void =>
    onSaveAction(WorkspacePresetAction.SAVE_AS, 'user', true);

  const onSave = (): void => {
    // Save without opening dialog
    dispatch(
      workspaceListActions.submitFormWorkspaceActionDialogOptions({
        presetAction: WorkspacePresetAction.SAVE,
        presetId: workspaceId,
        data: {
          ...workspaceData,
        },
        emptyMapPresetTitle: t('workspace-mappreset-new'),
        emptyWorkspacePresetTitle: t('workspace-new'),
      }),
    );
  };

  const onEdit = (): void => {
    const adminMetadata =
      isAdmin && auth?.username
        ? {
            updated: new Date().toISOString(),
            updatedBy: auth.username,
          }
        : {};

    setWorkspaceActionDialogOptions({
      action: WorkspacePresetAction.EDIT,
      presetId: workspaceId,
      formValues: {
        ...workspaceData,
        updated: new Date().toISOString(),
        ...adminMetadata,
      },
    });
  };

  const onSaveSystemPresetAs = (): void =>
    onSaveAction(WorkspacePresetAction.SAVE_SYSTEM_PRESET_AS, 'system');

  const onSaveSystemPreset = (): void =>
    onSaveAction(WorkspacePresetAction.SAVE_SYSTEM_PRESET, 'system');

  const onReset = (): void => {
    setWorkspaceActionDialogOptions({
      action: WorkspacePresetAction.RESET,
      presetId: workspaceId,
      formValues: { title, scope: isAdmin ? 'system' : 'user' },
    });
  };

  const onDeleteSystemPreset = (): void => {
    setWorkspaceActionDialogOptions({
      action: WorkspacePresetAction.DELETE_SYSTEM_PRESET,
      presetId: workspaceId,
      formValues: { title },
    });
  };

  const getOnDelete = (): undefined | (() => void) => {
    if (isNew) {
      return undefined; // to disable delete option
    }
    if (isUserPreset && isUserPresetCreator) {
      return onDelete;
    }
    return isAdmin ? onDeleteSystemPreset : undefined;
  };

  const handleSave = (): undefined | (() => void) => {
    if (isNew) {
      return undefined;
    }
    if (isUserPreset && isUserPresetCreator) {
      return onSave;
    }
    return isAdmin ? onSaveSystemPreset : undefined;
  };

  const handleSaveAs = (): undefined | (() => void) => {
    return isAdmin ? onSaveSystemPresetAs : onSaveAs;
  };

  const handleEdit = (): undefined | (() => void) => {
    if (isNew) {
      return undefined;
    }
    if (isAdmin) {
      return onEdit;
    }
    return isUserPreset && isUserPresetCreator ? onEdit : undefined;
  };

  const [isPasteDialogOpen, setIsPasteDialogOpen] = React.useState(false);

  const onCopyToClipboard = React.useCallback(async (): Promise<void> => {
    try {
      const workspaceWithLiveViews = getWorkspaceToSendToBackend(
        store.getState(),
      );
      const sanitized = sanitizeWorkspaceForClipboard(workspaceWithLiveViews);
      const json = JSON.stringify(sanitized, null, 2);
      await writeToClipboard(json);
      dispatch(
        snackbarActions.openSnackbar({
          type: snackbarTypes.SnackbarMessageType.VERBATIM_MESSAGE,
          message: t('workspace-copy-success'),
        }),
      );
    } catch {
      dispatch(
        snackbarActions.openSnackbar({
          type: snackbarTypes.SnackbarMessageType.VERBATIM_MESSAGE,
          message: t('workspace-error-generic'),
        }),
      );
    }
  }, [dispatch, store, t]);

  const onPasteFromClipboard = React.useCallback((): void => {
    setIsPasteDialogOpen(true);
  }, []);

  const onPasteConfirm = React.useCallback(
    (jsonString: string): void => {
      const parsed = JSON.parse(jsonString) as WorkspacePresetForBE;
      const sanitized = sanitizeWorkspaceForClipboard(parsed);
      const workspacePreset = getLoadingWorkspace(sanitized);
      dispatch(
        workspaceActions.changePreset({ workspacePreset, markAsChanged: true }),
      );
      dispatch(
        snackbarActions.openSnackbar({
          type: snackbarTypes.SnackbarMessageType.VERBATIM_MESSAGE,
          message: t('workspace-paste-success'),
        }),
      );
    },
    [dispatch, t],
  );

  return (
    <>
      <WorkspaceOptions
        handleCopyToClipboard={isNew ? undefined : onCopyToClipboard}
        handleDelete={getOnDelete()}
        handleEdit={handleEdit()}
        handlePasteFromClipboard={onPasteFromClipboard}
        handleReset={onReset}
        handleSave={handleSave()}
        handleSaveAs={handleSaveAs()}
        isDisabled={!viewIds.length || !isLoggedIn}
      />
      <PastePresetDialog
        isOpen={isPasteDialogOpen}
        mode="workspace"
        onClose={() => setIsPasteDialogOpen(false)}
        onConfirm={onPasteConfirm}
      />
    </>
  );
};
